/**
 * The form that brings a new set of prices into force.
 *
 * In a dialog rather than sitting permanently under the history, because it is not a
 * setting anybody arrives to change — the page is read far more often than it is written
 * to, and a form that is always open invites somebody to treat the numbers in it as the
 * numbers in force.
 *
 * It never edits a version. It posts a new one, and the previous closes at the same instant;
 * the reasoning is in the section beside it.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { TariffDraft } from '../api';
import { checkForm } from '../check';
import { Banner, Button, Dialog, Field, Input, Measured, Select, type FieldProblem } from '../../ui';
import type { VehicleClass } from '../../vehicles/api';
import { className } from '../../vehicles/words';

/** The form owner for the footer's submit button, which sits outside the form element. */
const FORM = 'new-tariff';

export function NewTariffDialog({
  open,
  draft,
  classes,
  saving,
  error,
  onChange,
  onClose,
  onSubmit,
  onInvalid,
}: {
  open: boolean;
  draft: TariffDraft;
  /** The firm's classes: a tariff prices one of them, or every one. */
  classes: readonly VehicleClass[];
  saving: boolean;
  error: string | null;
  onChange: <K extends keyof TariffDraft>(key: K, value: TariffDraft[K]) => void;
  onClose: () => void;
  onSubmit: () => void;
  /** The form was not sent: these fields would have been refused. */
  onInvalid: (problem: FieldProblem) => void;
}) {
  const { t } = useTranslation();
  const form = useRef<HTMLFormElement>(null);

  return (
    <Dialog
      open={open}
      wide
      title={t('tariffs.introduce')}
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t('settings.close')}
          </Button>
          <Button variant="primary" type="submit" form={FORM} disabled={saving}>
            {saving ? t('settings.saving') : t('tariffs.introduceAction')}
          </Button>
        </>
      }
    >
      <p className="text-secondary text-ink-3 mt-0 mb-3.5 leading-[1.4]">
        {t('tariffs.introduceHelp')}
      </p>

      {error !== null && (
        <Banner tone="danger" className="mb-3.5">
          {error}
        </Banner>
      )}

      {/* `noValidate`: the browser's own bubble would stop the submit over one field at a
          time, in the browser's language. `checkForm` asks the same questions of every field
          at once and marks each where it sits. */}
      <form
        ref={form}
        id={FORM}
        noValidate
        className="grid grid-cols-2 gap-4"
        onSubmit={(event) => {
          event.preventDefault();

          const problem = checkForm(form.current, t);

          if (problem === null) onSubmit();
          else onInvalid(problem);
        }}
      >
        <Field id="displayName" label={t('tariffs.displayName')} help={t('tariffs.displayNameHelp')}>
          <Input
            value={draft.displayName}
            onChange={(e) => onChange('displayName', e.target.value)}
            required
          />
        </Field>

        <Field id="vehicleCategoryCode" label={t('tariffs.category')} help={t('tariffs.categoryHelp')}>
          {/* Chosen from the firm's classes rather than typed: a code typed wrong was a
              tariff that never applied to anything, and nothing said so. */}
          <Select
            value={draft.vehicleCategoryCode ?? ''}
            onChange={(e) =>
              onChange('vehicleCategoryCode', e.target.value === '' ? null : e.target.value)
            }
          >
            <option value="">{t('tariffs.everyClass')}</option>
            {classes.map((c) => (
              <option key={c.code} value={c.code}>
                {className(t, c.code, c.displayName)}
              </option>
            ))}
            {draft.vehicleCategoryCode !== null &&
              !classes.some((c) => c.code === draft.vehicleCategoryCode) && (
                <option value={draft.vehicleCategoryCode}>{draft.vehicleCategoryCode}</option>
              )}
          </Select>
        </Field>

        <Amount
          field="baseFare"
          currency={draft.currency}
          value={draft.baseFare}
          onChange={(value) => onChange('baseFare', value)}
        />
        <Amount
          field="perKilometre"
          currency={draft.currency}
          step={0.0001}
          value={draft.perKilometre}
          onChange={(value) => onChange('perKilometre', value)}
        />
        <Amount
          field="perMinute"
          currency={draft.currency}
          step={0.0001}
          value={draft.perMinute}
          onChange={(value) => onChange('perMinute', value)}
        />
        <Amount
          field="perWaitingMinute"
          currency={draft.currency}
          step={0.0001}
          value={draft.perWaitingMinute}
          onChange={(value) => onChange('perWaitingMinute', value)}
        />
        <Amount
          field="minimumFare"
          currency={draft.currency}
          value={draft.minimumFare}
          onChange={(value) => onChange('minimumFare', value)}
        />
        <Amount
          field="roundingIncrement"
          currency={draft.currency}
          min={0.01}
          value={draft.roundingIncrement}
          onChange={(value) => onChange('roundingIncrement', value)}
        />

        <Field
          id="freeWaitingMinutes"
          label={t('tariffs.freeWaitingMinutes')}
          help={t('tariffs.freeWaitingMinutesHelp')}
        >
          <Measured
            unit={t('settings.unitMinutes')}
            type="number"
            required
            min={0}
            max={60}
            value={Number.isNaN(draft.freeWaitingMinutes) ? '' : draft.freeWaitingMinutes}
            onChange={(e) => onChange('freeWaitingMinutes', e.target.valueAsNumber)}
          />
        </Field>

        <Field id="currency" label={t('tariffs.currency')} help={t('tariffs.currencyHelp')}>
          <Input
            value={draft.currency}
            maxLength={3}
            onChange={(e) => onChange('currency', e.target.value.toUpperCase())}
            required
          />
        </Field>

        <Field
          id="effectiveFrom"
          className="col-span-2"
          label={t('tariffs.effectiveFrom')}
          help={t('tariffs.effectiveFromHelp')}
        >
          <Input
            type="datetime-local"
            value={draft.effectiveFrom ?? ''}
            onChange={(e) => onChange('effectiveFrom', e.target.value === '' ? null : e.target.value)}
          />
        </Field>
      </form>
    </Dialog>
  );
}

/**
 * One money field, labelled and explained from its own name.
 *
 * `field` is both the translation key and what the number is; renaming one renames the
 * other, and the key test does not cover a key built from a template literal.
 */
function Amount({
  field,
  currency,
  value,
  step = 0.01,
  min = 0,
  onChange,
}: {
  field: string;
  currency: string;
  value: number;
  step?: number;
  /** The smallest the server takes: zero for a charge, a cent for the rounding step. */
  min?: number;
  onChange: (value: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <Field id={field} label={t(`tariffs.${field}`)} help={t(`tariffs.${field}Help`)}>
      <Measured
        unit={currency}
        type="number"
        required
        min={min}
        step={step}
        value={Number.isNaN(value) ? '' : value}
        onChange={(e) => onChange(e.target.valueAsNumber)}
      />
    </Field>
  );
}
